///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One storm-centered IR frame decoded to a CPU cold-index grid, for the
/// storm column: the lid samples it as an R8 texture, the wall sections
/// and the IR statistics read it directly. The 1024 px frame is reduced to
/// 512 px on decode (about 3.5 km per pixel in a 16° box), enough to
/// resolve an eye and cheap enough to keep a couple of dozen frames.
///

import { type StormCloudProperties } from "@/earthbrowser/dataset-distribution/clouds/StormCloudsTable"
import { ImageLoader } from "@/earthbrowser/utils/loaders/ImageLoader"
import { cCacheAgeOneWeek } from "@/earthbrowser/utils/loaders/FetchLoader"
import { datasetUrl } from "@/utils/datasetUrl"
import { ComponentType, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { Texture } from "@stovokor/shader/Texture"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { analyzeColdIndexGrid, sampleColdIndex, type ColdIndexGrid, type IrStructure } from "./stormColumnStats"

const cGridSize = 512

export class StormCloudField implements ColdIndexGrid {
	public readonly size = cGridSize
	public readonly data: Uint8Array
	public readonly minLon: number
	public readonly minLat: number
	public readonly maxLon: number
	public readonly maxLat: number
	public readonly timeMs: number
	public readonly timeIso: string
	public readonly satellite: string
	private texture?: Texture
	private structureCache?: IrStructure

	private constructor(row: StormCloudProperties, data: Uint8Array) {
		this.data = data
		this.minLon = row.min_lon
		this.minLat = row.min_lat
		this.maxLon = row.max_lon
		this.maxLat = row.max_lat
		this.timeIso = row.time
		this.timeMs = Date.parse(row.time)
		this.satellite = row.satellite
	}

	/** Fetch (through the image cache) and decode a frame; undefined on failure */
	public static async load(row: StormCloudProperties): Promise<StormCloudField | undefined> {
		const loader = new ImageLoader(datasetUrl(row.url), { lifetime: cCacheAgeOneWeek, priority: "high" })
		const image = await loader.loadImage()
		if (!image) return undefined
		const canvas = new OffscreenCanvas(cGridSize, cGridSize)
		const ctx = canvas.getContext("2d", { willReadFrequently: true })
		if (!ctx) return undefined
		ctx.imageSmoothingEnabled = true
		ctx.imageSmoothingQuality = "high"
		ctx.drawImage(image, 0, 0, cGridSize, cGridSize)
		const pixels = ctx.getImageData(0, 0, cGridSize, cGridSize).data
		const data = new Uint8Array(cGridSize * cGridSize)
		for (let i = 0; i < data.length; i++) {
			data[i] = pixels[i * 4]
		}
		return new StormCloudField(row, data)
	}

	/** Storm position at scan time: the frame box is cut around it */
	public get centerLat(): number {
		return (this.minLat + this.maxLat) / 2
	}

	public get centerLon(): number {
		let lon = (this.minLon + this.maxLon) / 2
		if (lon > 180) lon -= 360
		return lon
	}

	/** Cold index 0..1 at a lat/lon, 0 outside the box */
	public sample(lat: number, lon: number): number {
		return sampleColdIndex(this, lat, lon)
	}

	public get structure(): IrStructure {
		if (!this.structureCache) this.structureCache = analyzeColdIndexGrid(this)
		return this.structureCache
	}

	/** R8 texture of the grid for the lid's vertex displacement (created on first use) */
	public getTexture(context: StovokorContext): Texture {
		if (!this.texture) {
			this.texture = new Texture(context, 1, 1, InternalFormat.R8, PixelFormat.Red, ComponentType.UnsignedByte,
				TextureFilter.Linear, TextureFilter.Linear, TextureWrap.ClampToEdge, TextureWrap.ClampToEdge)
			this.texture.uploadRawData(this.data, cGridSize, cGridSize, InternalFormat.R8, PixelFormat.Red, ComponentType.UnsignedByte)
		}
		return this.texture
	}

	public destroy(): void {
		this.texture?.destroy()
		this.texture = undefined
	}
}
